import { useEffect, useMemo, useState } from 'react';
import { useTheme } from './ThemeProvider';
import api from '../services/api';

export const MOTIVOS_RAPIDOS_RETROCESO = [
  'Encargo de venta está mal',
  'Las fotos no son adecuadas',
  'Datos erróneos',
];

function presetSugeridoDesdeRetroceso({ motivo, camposFaltantes }) {
  const keys = new Set((camposFaltantes || []).map((c) => c.campo_key));
  if (motivo === 'fotos_marco' || motivo === 'fotos' || keys.has('fotos')) {
    return 'Las fotos no son adecuadas';
  }
  if (motivo === 'encargo' || keys.has('firma_captacion')) return 'Encargo de venta está mal';
  if (motivo === 'campos_agente' || keys.size > 0) return 'Datos erróneos';
  return '';
}

export default function ModalMotivoRetrocesoCaptacion({
  isOpen,
  captacion,
  motivoRetroceso,
  camposFaltantes,
  onConfirm,
  onClose,
}) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === 'dark';
  const [preset, setPreset] = useState('');
  const [textoLibre, setTextoLibre] = useState('');
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const presetInicial = useMemo(
    () => presetSugeridoDesdeRetroceso({ motivo: motivoRetroceso, camposFaltantes }),
    [motivoRetroceso, camposFaltantes],
  );

  useEffect(() => {
    if (!isOpen) return;
    setPreset(presetInicial);
    setTextoLibre('');
    setError('');
    setGuardando(false);
  }, [isOpen, captacion?.id, presetInicial]);

  if (!isOpen || !captacion?.id) return null;

  const motivoFinal = () => {
    const libre = String(textoLibre || '').trim();
    const pre = String(preset || '').trim();
    if (libre && pre) return `${pre}. ${libre}`;
    return libre || pre;
  };

  const confirmar = async () => {
    const motivo = motivoFinal();
    if (!motivo) {
      setError('Indica el motivo: elige una respuesta rápida o escribe el detalle.');
      return;
    }
    setGuardando(true);
    setError('');
    try {
      await api.post(`/captaciones/${captacion.id}/historial`, {
        tipo: 'cambio_etapa',
        contenido: `Devuelta a Visita concertada — ${motivo}`,
        metadata: {
          etapa_destino: 'visita_cap',
          etapa_origen: 'revisar_cap',
          retroceso_manual_motivo: true,
          motivo_rapido: preset || null,
          detalle_libre: String(textoLibre || '').trim() || null,
          motivo_sistema: motivoRetroceso || null,
          campos_faltantes: camposFaltantes || [],
        },
      });
      onConfirm?.({ motivo, captacion });
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Error guardando el motivo');
      setGuardando(false);
    }
  };

  const refLabel = captacion.ref
    || captacion.datos_extras?.ref
    || [captacion.calle, captacion.numero].filter(Boolean).join(' ')
    || 'Captación';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
      onClick={() => { if (!guardando) onClose?.(); }}
    >
      <div
        style={{
          background: c.bgPanel,
          borderRadius: 14,
          padding: 22,
          width: '100%',
          maxWidth: 520,
          border: `1px solid ${c.border}`,
          boxShadow: isDark ? '0 16px 48px rgba(0,0,0,0.5)' : '0 16px 48px rgba(0,0,0,0.12)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: c.textPrimary }}>
              Volver a Visita concertada
            </div>
            <div style={{ fontSize: 12, color: c.textMuted, marginTop: 4 }}>{refLabel}</div>
          </div>
          <button
            type="button"
            disabled={guardando}
            onClick={() => onClose?.()}
            style={{
              background: 'none',
              border: 'none',
              color: c.textMuted,
              fontSize: 22,
              cursor: guardando ? 'not-allowed' : 'pointer',
              lineHeight: 1,
              opacity: guardando ? 0.5 : 1,
            }}
          >
            ×
          </button>
        </div>

        <p style={{ fontSize: 13, color: c.textSecondary, margin: '12px 0 14px', lineHeight: 1.5 }}>
          Esta captación volverá a la etapa Visita concertada. Indica el motivo para que el agente pueda darle solución.
        </p>

        <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, marginBottom: 8, textTransform: 'uppercase' }}>
          Respuestas rápidas
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
          {MOTIVOS_RAPIDOS_RETROCESO.map((m) => {
            const activo = preset === m;
            return (
              <button
                key={m}
                type="button"
                disabled={guardando}
                onClick={() => {
                  setPreset(activo ? '' : m);
                  setError('');
                }}
                style={{
                  padding: '7px 11px',
                  fontSize: 12,
                  fontWeight: 500,
                  borderRadius: 8,
                  border: `1px solid ${activo ? '#ef4444' : c.border}`,
                  background: activo
                    ? (isDark ? 'rgba(239,68,68,0.2)' : 'rgba(239,68,68,0.08)')
                    : c.bgInput,
                  color: activo ? '#ef4444' : c.textSecondary,
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  opacity: guardando ? 0.7 : 1,
                }}
              >
                {m}
              </button>
            );
          })}
        </div>

        <label style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, display: 'block', marginBottom: 6 }}>
          Ampliar o escribir otro motivo
        </label>
        <textarea
          value={textoLibre}
          disabled={guardando}
          onChange={(e) => {
            setTextoLibre(e.target.value);
            setError('');
          }}
          rows={3}
          placeholder="Detalle para el agente…"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '10px 12px',
            borderRadius: 8,
            border: `1px solid ${error ? c.accentRed : c.border}`,
            background: c.bgInput,
            color: c.textPrimary,
            fontSize: 13,
            resize: 'vertical',
            fontFamily: 'inherit',
            lineHeight: 1.45,
          }}
        />
        {error && (
          <p style={{ fontSize: 12, color: c.accentRed, marginTop: 8, marginBottom: 0 }}>{error}</p>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button
            type="button"
            disabled={guardando}
            onClick={() => onClose?.()}
            style={{
              padding: '9px 16px',
              borderRadius: 8,
              border: `1px solid ${c.border}`,
              background: 'transparent',
              color: c.textSecondary,
              fontSize: 13,
              cursor: guardando ? 'not-allowed' : 'pointer',
              opacity: guardando ? 0.6 : 1,
            }}
          >
            Omitir
          </button>
          <button
            type="button"
            disabled={guardando}
            onClick={confirmar}
            style={{
              padding: '9px 18px',
              borderRadius: 8,
              border: 'none',
              background: '#ef4444',
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              cursor: guardando ? 'wait' : 'pointer',
              opacity: guardando ? 0.85 : 1,
            }}
          >
            {guardando ? 'Guardando…' : 'Confirmar y cerrar'}
          </button>
        </div>
      </div>
    </div>
  );
}
